/**
 * Pure-function handler describing what should happen when the user
 * taps "Unblock" on a row of the Blocked-users screen.
 *
 * Threat model:
 *
 * The unblock action mutates client state (BlockListStore) and emits a
 * sync payload to the user's other linked devices. Critically, the
 * view-model layer DOES NOT re-implement the crypto for that sync — it
 * delegates to `BlockListStore.unblock(...)` which already returns the
 * canonical `BlockSyncPayload`. Centralising the cryptographic surface in
 * `BlockListStore` means a future change to the sync schema or signing
 * requirement only has to land in one place.
 *
 * Idempotency: calling `unblock` for a peer that is not currently blocked
 * (e.g. a stale UI snapshot, or a sibling device already synced an
 * unblock) is a no-op and surfaces `'skipNotBlocked'` rather than emitting
 * a payload — emitting an unblock for a peer that is not currently blocked
 * locally would be a wasted side-channel signal and would also confuse a
 * sibling device's last-writer-wins merge if our clock had drifted.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockedUsersUnblockHandler.swift`.
 */

import type { BlockSyncPayload } from "../block-list/BlockListEntry";
import type { BlockListStore } from "../block-list/BlockListStore";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Outcome of an "Unblock" action.
 *
 * - `'unblocked'` — the peer was blocked; the store has been mutated
 *   and the returned `payload` should be shipped to our linked devices
 *   via the existing E2EE pairwise channel.
 * - `'skipNotBlocked'` — the peer was not blocked; UI swallows the tap
 *   and refreshes the list. No store mutation, no network egress.
 */
export type UnblockOutcome =
  | { readonly kind: "unblocked"; readonly payload: BlockSyncPayload }
  | { readonly kind: "skipNotBlocked" };

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

/**
 * Decide what the UI should do for an "Unblock" action on `userId`.
 *
 * `ts` exists for tests; production callers omit it so the store
 * records the unblock timestamp at the moment of the user's gesture.
 *
 * Returns `'skipNotBlocked'` if the user is not currently blocked (idempotent).
 */
export async function unblock(
  userId: string,
  store: BlockListStore,
  ts?: number,
): Promise<UnblockOutcome> {
  const blocked = await store.isBlocked(userId);
  if (!blocked) {
    return { kind: "skipNotBlocked" };
  }
  const payload = await store.unblock(userId, ts);
  return { kind: "unblocked", payload };
}
